// src/components/Dashboard.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Dashboard.css";
import bgVideo from "../assets/bluegrad.mp4";
import logo from "../assets/logocl.png";
import { fetchReviewStats, fetchEmployeePerformance, fetchEmployeeNLB } from "../api/reviews";
// import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend ,Cell} from 'recharts';
import ReviewsModal from '../components/ReviewsModal';
const notificationsList = [
    "New review added!",
    "Settings were saved.",
    "User signed up!",
    "New message received.",
    "System update available."
];

export default function Dashboard() {
    const [notifications, setNotifications] = useState([]);
    const [reviewStats, setReviewStats] = useState({
        total: 0,
        positive: 0,
        negative: 0,
        neutral: 0,
        averageRating: 0
    });
    const [employeeData, setEmployeeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [employeeNLBData, setEmployeeNLBData] = useState({});
    const [nlbLoading, setNlbLoading] = useState({});
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showReviewsModal, setShowReviewsModal] = useState(false);

    // Move chartData inside the component where reviewStats is available
    const chartData = [
        { name: 'Positive', value: reviewStats.positive, color: 'rgba(87, 204, 153, 0.9)' },
        { name: 'Neutral', value: reviewStats.neutral, color: 'rgba(255, 193, 7, 0.9)' },
        { name: 'Negative', value: reviewStats.negative, color: 'rgba(255, 107, 107, 0.9)' }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            const first = notificationsList[Math.floor(Math.random() * notificationsList.length)];
            let second = notificationsList[Math.floor(Math.random() * notificationsList.length)];
            while (second === first) {
                second = notificationsList[Math.floor(Math.random() * notificationsList.length)];
            }
            setNotifications([first, second]);
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setTableLoading(true);
                
                // Load stats and employee performance
                const [stats, employeePerformance] = await Promise.all([
                    fetchReviewStats(),
                    fetchEmployeePerformance()
                ]);
                
                setReviewStats(stats);
                setEmployeeData(employeePerformance.employees || []);
                
                // Load NLB data for each employee
                if (employeePerformance.employees) {
                    const nlbPromises = employeePerformance.employees.map(async (employee) => {
                        try {
                            setNlbLoading(prev => ({ ...prev, [employee.sub]: true }));
                            const nlbData = await fetchEmployeeNLB(employee.sub);
                            setEmployeeNLBData(prev => ({ ...prev, [employee.sub]: nlbData }));
                        } catch (error) {
                            console.error(`Failed to load NLB for ${employee.name}:`, error);
                            setEmployeeNLBData(prev => ({ ...prev, [employee.sub]: null }));
                        } finally {
                            setNlbLoading(prev => ({ ...prev, [employee.sub]: false }));
                        }
                    });
                    
                    await Promise.all(nlbPromises);
                }
            } catch (err) {
                console.error('Error loading data:', err);
                setError('Failed to load dashboard data');
                setReviewStats({
                    total: 0,
                    positive: 0,
                    negative: 0,
                    neutral: 0,
                    averageRating: 0
                });
                setEmployeeData([]);
            } finally {
                setLoading(false);
                setTableLoading(false);
            }
        };

        loadData();
    }, []);

    const handleViewReviews = (employee) => {
        console.log('Viewing reviews for employee:', employee.name, 'Sub:', employee.sub);
        setSelectedEmployee(employee);
        setShowReviewsModal(true);
    };

    const handleCloseModal = () => {
        setShowReviewsModal(false);
        setSelectedEmployee(null);
    };

    if (loading) {
        return (
            <div className="app">
                <video autoPlay loop muted className="bg-video">
                    <source src={bgVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="overlay">
                    <div style={{ color: 'white', fontSize: '1.5rem' }}>Loading dashboard...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="app">
            <video autoPlay loop muted className="bg-video">
                <source src={bgVideo} type="video/mp4" />
                Your browser does not support the video tag.
            </video>

            <div className="overlay">
                {/* Statistics Tiles */}
                <div className="stats-container">
                    <div className="stat-tile">
                        <div className="stat-icon">📊</div>
                        <div className="stat-content">
                            <div className="stat-number">{reviewStats.total}</div>
                            <div className="stat-label">Total Reviews</div>
                        </div>
                    </div>
                    
                    <div className="stat-tile positive">
                        <div className="stat-icon">👍</div>
                        <div className="stat-content">
                            <div className="stat-number">{reviewStats.positive}</div>
                            <div className="stat-label">Positive</div>
                        </div>
                    </div>
                    
                    <div className="stat-tile negative">
                        <div className="stat-icon">👎</div>
                        <div className="stat-content">
                            <div className="stat-number">{reviewStats.negative}</div>
                            <div className="stat-label">Negative</div>
                        </div>
                    </div>
                    
                    <div className="stat-tile neutral">
                        <div className="stat-icon">😐</div>
                        <div className="stat-content">
                            <div className="stat-number">{reviewStats.neutral}</div>
                            <div className="stat-label">Neutral</div>
                        </div>
                    </div>
                    
                    <div className="stat-tile rating">
                        <div className="stat-icon">⭐</div>
                        <div className="stat-content">
                            <div className="stat-number">{reviewStats.averageRating}</div>
                            <div className="stat-label">Avg Rating</div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div style={{ 
                        color: '#ff6b6b', 
                        backgroundColor: 'rgba(255, 107, 107, 0.1)', 
                        padding: '10px 20px', 
                        borderRadius: '8px',
                        marginBottom: '20px'
                    }}>
                        {error}
                    </div>
                )}

                {/* Main Content Grid */}
                <div className="main-content-grid">
                    {/* Left Side - Chart */}
                    <div className="chart-section">
                        <div className="chart-card">
                            {/* <h3>Review Distribution</h3>
                            <div className="chart-container">
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie 
                                            data={chartData} 
                                            dataKey="value" 
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            innerRadius={40}
                                            fill="#8884d8"
                                        >
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                border: 'none',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer> */}
                            {/* </div> */}
                            <h3>Review Distribution</h3>
<div className="chart-container">
 <ResponsiveContainer width="100%" height={250}>
  <BarChart
    data={chartData}
    barCategoryGap="25%"   // space between categories
    barGap={8}             // space between bars (not critical for single series)
    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
  >
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="name" />
    <YAxis allowDecimals={false} />
    <Tooltip /* ...styles... */ />
    <Legend />
   <Bar dataKey="value" radius={[6, 6, 0, 0]}>
  {chartData.map((entry, index) => (
    <Cell
      key={`bar-cell-${index}`}
      fill={
        entry.color ||
        (entry.name === 'Positive' ? '#57CC99' :
         entry.name === 'Neutral'  ? '#FFC107' :
                                     '#FF6B6B')
      }
    />
  ))}
</Bar>
  </BarChart>
</ResponsiveContainer>
</div>
                            {/* <div className="chart-legend">
                                {chartData.map((entry, index) => (
                                    <div key={index} className="legend-item">
                                        <div 
                                            className="legend-color" 
                                            style={{ backgroundColor: entry.color }}
                                        ></div>
                                        <span className="legend-label">{entry.name}</span>
                                        <span className="legend-value">({entry.value})</span>
                                    </div>
                                ))}
                            </div> */}
                            {/* <div className="chart-legend">
  {chartData.map((entry, index) => (
    <div key={index} className="legend-item">
      <div className="legend-color" style={{ backgroundColor: entry.color }}></div>
      <span className="legend-label">{entry.name}</span>
      <span className="legend-value">({entry.value})</span>
    </div>
  ))}
</div> */}
                        </div>
                    </div>

                    {/* Right Side - Quick Stats */}
                    <div className="info-section">
                        <div className="info-card">
                            <h3>Quick Stats</h3>
                            <div className="quick-stats">
                                <div className="quick-stat-item">
                                    <div className="quick-stat-label">Total Reviews</div>
                                    <div className="quick-stat-value">{reviewStats.total}</div>
                                </div>
                                <div className="quick-stat-item">
                                    <div className="quick-stat-label">Positive Rate</div>
                                    <div className="quick-stat-value">
                                        {reviewStats.total > 0 ? Math.round((reviewStats.positive / reviewStats.total) * 100) : 0}%
                                    </div>
                                </div>
                                <div className="quick-stat-item">
                                    <div className="quick-stat-label">Average Rating</div>
                                    <div className="quick-stat-value">{reviewStats.averageRating}/5</div>
                                </div>
                                <div className="quick-stat-item">
                                    <div className="quick-stat-label">Total Employees</div>
                                    <div className="quick-stat-value">{employeeData.length}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Employee Performance Table */}
                <div className="table-container">
                    <div className="table-card">
                        <h3>Employee Performance Overview</h3>
                        {tableLoading ? (
                            <div className="table-loading">Loading employee data...</div>
                        ) : (
                            <div className="table-wrapper">
                                <table className="reviews-table">
                                    <thead>
                                        <tr>
                                            <th>Serial No</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Total Reviews</th>
                                            <th>Average Rating</th>
                                            <th>Latest Review</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employeeData.length > 0 ? (
                                            employeeData.map((employee, index) => {
                                                const nlbData = employeeNLBData[employee.sub];
                                                const isLoading = nlbLoading[employee.sub];
                                                
                                                return (
                                                    <tr key={employee.id}>
                                                        <td>{index + 1}</td>
                                                        <td>{employee.name}</td>
                                                        <td>{employee.email}</td>
                                                        <td>{employee.total_reviews}</td>
                                                        <td>
                                                            <div className="rating-display">
                                                                <span className="rating-number">{employee.average_rating}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="latest-review">
                                                                {employee.latest_review ? (
                                                                    <div className="review-preview">
                                                                        <div className="review-rating-small">
                                                                            {employee.latest_review.rating}
                                                                        </div>
                                                                        <div className="review-text-preview" title={employee.latest_review.text}>
                                                                            {employee.latest_review.text.length > 50 
                                                                                ? employee.latest_review.text.substring(0, 50) + '...'
                                                                                : employee.latest_review.text
                                                                            }
                                                                        </div>
                                                                        <div className="review-date-small">
                                                                            {employee.latest_review.date}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="no-review">No reviews</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <button 
                                                                className="view-reviews-btn"
                                                                onClick={() => handleViewReviews(employee)}
                                                                disabled={employee.total_reviews === 0}
                                                            >
                                                                View Reviews ({employee.total_reviews})
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="no-data">No employees found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Reviews Modal */}
                <ReviewsModal
                    isOpen={showReviewsModal}
                    onClose={handleCloseModal}
                    employeeSub={selectedEmployee?.sub}
                    employeeName={selectedEmployee?.name}
                />
            </div>
        </div>
    );
}
